import { DeleteIcon, HelpIcon } from '@sketchnote/icons';
import {
  Avatar,
  Button,
  UserSelectDropDownIndicator,
  UserSelectMultiValueLabel_Approval,
  UserSelectOption_custom,
  UserSelectSingleValue_custom,
  userSelectStyles_Custom_Approval,
} from '@sketchnote/shared-components';
import Tippy from '@tippyjs/react';
import * as React from 'react';
import Select from 'react-select';
import { useBoardStore } from 'src/features/boards';
import { EmptyStateDashboard } from 'src/features/dashboard/components/EmptyState';
import { useAuth } from 'src/shared/hooks/use-auth';
import { getMemberDetails } from 'src/utils/getMemberDetails';

import { useTasksStore } from '../store/useTasksStore';
import { TaskT } from '../types/tasks';
import { ApprovalsHistory } from './ApprovalComponents';

const { useState, useEffect } = React;

export const Approvals = ({ item: task, members }: { item: TaskT; members: any }) => {
  const { showHistory, isFullScreen, onFetchApprovalInfo } = useTasksStore((state) => state);
  useEffect(() => {
    if (task?.id) {
      onFetchApprovalInfo(task?.id);
    }
  }, [task?.id]);

  return (
    <div className="overflow-hidden py-8 h-full">
      {task?.approval?.approvalHistory?.length !== 0 && showHistory ? (
        <ApprovalsHistory item={task} />
      ) : (
        <ApprovalsSettings item={task} members={members} />
      )}
    </div>
  );
};

export const ApprovalsSettings = ({ item: task, members }: { item: TaskT; members: any }) => {
  const authData = useAuth((s) => s.userDetails);
  const { onSendForApproval, onUpdateTaskHistory, setShowHistory } = useTasksStore(
    (state) => state
  );
  const boardsStore = useBoardStore();
  const [minApprovers, setMinimumApprovers] = useState<number>(1);

  //to be replaced with liveblocks
  const [approvers, setApprovers] = useState<any>([]);
  const [removedApprovers, setRemovedApprovers] = useState<any>([]);
  const [addedApprovers, setAddedApprovers] = useState<any>([]);
  useEffect(() => {
    if (task?.approval?.approvers) {
      setApprovers(task?.approval?.approvers.map((obj) => getMemberDetails(obj?.member)));
    }
    if (task?.approval?.minApprovalsRequired) {
      setMinimumApprovers(task?.approval?.minApprovalsRequired);
    }
  }, [task?.approval?.approvers, task?.approval?.minApprovalsRequired]);

  const handleSubmitForApproval = () => {
    const approversIds = approvers?.map((obj) => obj.memberId);
    if (task?.id) {
      onSendForApproval(task?.id, approversIds, addedApprovers, removedApprovers, minApprovers);
      setShowHistory(true);
      if (addedApprovers.filter(Boolean).length > 0) {
        onUpdateTaskHistory(task?.id, {
          action: 'update_add_approvers',
          members: addedApprovers,
          member: authData?.memberInformation?.memberId || '',
          timestamp: '',
        });
      }
      if (removedApprovers.filter(Boolean).length > 0) {
        onUpdateTaskHistory(task?.id, {
          action: 'update_remove_approvers',
          members: removedApprovers,
          member: authData?.memberInformation?.memberId || '',
          timestamp: '',
        });
      }
    }
  };
  const assigneeOptions = React.useMemo(
    () => [
      {
        label: 'Select people',
        options:
          members?.map((obj) => ({
            label: obj.firstname,
            value: obj.memberId,
            memberId: obj.memberId,
            avatar: obj.avatar,
          })) || [],
      },
    ],
    []
  );
  const handleUpdateApprovers = (values) => {
    //value will always have one item, since value to Select dropdown is null
    const duplicateMember = approvers.find((item) => item.memberId === values[0].memberId);
    if (duplicateMember) return;
    const selectedApprovers = values?.map((obj) => ({
      firstname: obj.label,
      memberId: obj.memberId,
      avatar: obj.avatar,
      lastname: obj.lastname,
    }));
    setAddedApprovers([...addedApprovers, ...selectedApprovers?.map((item) => item.memberId)]);
    setApprovers([...approvers, ...selectedApprovers]);
  };

  const handleDeleteApprover = (memberId: string) => {
    if (task?.approval?.approvers?.find((item) => item.member === memberId)) {
      setRemovedApprovers([...removedApprovers, memberId]);
    }

    setApprovers((prev) => prev.filter((obj) => obj.memberId !== memberId));
    setAddedApprovers((prev) => prev.filter((obj) => obj !== memberId));
  };

  return (
    <div className="flex flex-col h-full">
      <section className="px-12">
        <span className="mb-8 block text-xs text-gray-999 font-medium">Approval settings</span>
        <span className="text-xs  inline-block mb-6 text-gray-750">
          Choose users who can add approvals to this task{' '}
        </span>
        <Tippy
          content={'Users chosen here will receive approval requests on email'}
          className="w-210 bg-white border p-2 text-gray-999 rounded-sm text-xs"
          arrow={false}
        >
          <span>
            <HelpIcon className="text-gray-500 -mt-4 ml-2 inline-block text-xs" />
          </span>
        </Tippy>
        <Select
          options={assigneeOptions}
          menuPortalTarget={document.body}
          hideSelectedOptions={true}
          isDisabled={task.isReadOnly || task?.access === 'view' || boardsStore.isPublicView}
          styles={userSelectStyles_Custom_Approval}
          onChange={handleUpdateApprovers}
          placeholder="Search for users"
          isMulti
          value={null}
          components={{
            DropdownIndicator:
              task?.access === 'view' || boardsStore.isPublicView
                ? null
                : UserSelectDropDownIndicator,
            IndicatorSeparator: () => null,
            SingleValue: UserSelectSingleValue_custom,
            Option: UserSelectOption_custom,
            MultiValueLabel: UserSelectMultiValueLabel_Approval,
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              //onchange
            }
          }}
        />
      </section>
      <section className="flex-grow py-8 flex flex-col px-12 overflow-scroll">
        <div className="flex-grow">
          {approvers && approvers?.length === 0 ? (
            //Fix emoty state
            <EmptyStateDashboard
              className="w-125"
              wrapperClass=""
              textWrapperClass="text-sm"
              contents={['Nothing to see here... yet!', 'Add approvers to tasks here']}
              src="https://res.cloudinary.com/samvibes/image/upload/empty%20states/approval_empty_state.png"
            />
          ) : (
            approvers.filter(Boolean)?.map((obj, index) => (
              <div
                className="flex items-center justify-between my-16 ml-2"
                key={`${index}-${obj.memberId}`}
              >
                <div className="flex">
                  <Avatar src={obj.avatar} type="photo" size="sm" name={obj.label} />
                  <span className="text-sm text-gray-750 inline-block ml-4">{obj.firstname}</span>
                </div>
                <button onClick={() => handleDeleteApprover(obj.memberId)}>
                  <DeleteIcon className="text-gray-750" />
                </button>
              </div>
            ))
          )}
        </div>
      </section>

      <section>
        {approvers?.length !== 0 && (
          <div className="px-12">
            <div className="mb-8 flex items-center justify-between text-sm text-gray-999">
              <div>
                Minimum approvals required{' '}
                {minApprovers > approvers?.length && (
                  <div className="text-xss text-red-900">
                    Add more approvers or reduce the number of approvals
                  </div>
                )}
              </div>
              <input
                type="number"
                max={approvers?.length || 0}
                min={1}
                onKeyDown={(e) => {
                  e.preventDefault();
                }}
                value={minApprovers}
                onChange={(e) => {
                  setMinimumApprovers(parseInt(e.target.value));
                }}
                className={`text-sm rounded-sm w-84 h-32 px-6 ${
                  minApprovers > approvers?.length
                    ? 'bg-red-100 border border-red-500 text-red-800'
                    : 'bg-gray-100 border text-gray-999 '
                }`}
              />
            </div>
          </div>
        )}
        <div className="pt-14 px-12 items-center flex justify-end border-t bg-gray-199">
          {task?.approval?.approvalHistory && task?.approval?.approvalHistory?.length > 0 ? (
            <Button
              status="secondary-gray"
              onClick={() => setShowHistory(true)}
              className="text-xs"
            >
              Cancel
            </Button>
          ) : (
            <></>
          )}
          <Button
            className="ml-6 border"
            onClick={handleSubmitForApproval}
            disabled={
              (removedApprovers.length === 0 &&
                addedApprovers.length === 0 &&
                minApprovers === task?.approval?.minApprovalsRequired) ||
              minApprovers > approvers.length
            }
          >
            Save and send request
          </Button>
        </div>
      </section>
    </div>
  );
};
